import { FC, useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { Claims } from '@/_shared/constants/claims';
import { openTabForAsyncRedirect } from '@/_shared/helpers/openTabForAsyncRedirect';
import { useDialog } from '@/_shared/hooks';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getBillingLinkCall } from '@/billing/state/api/getBillingLinkCall';
import type { SubscriptionPlanType } from '@/billing/state/types/subscription';
import type { DialogContactsPayload } from '@/core/components/DialogContacts/DialogContacts';
import { PlanType } from '@/swagger/constants/slash-enums';
import { DetailItem } from './DetailItem';
import { IncludedInPlan } from './IncludedInPlan';

type Props = {
  activationDateLabel: string;
  billingTypeLabel: string;
  paymentMethodLabel: string;
  nextInvoiceLabel: string;
  planType?: SubscriptionPlanType;
};

export const PlanDetailsCard: FC<Props> = ({
  activationDateLabel,
  billingTypeLabel,
  paymentMethodLabel,
  nextInvoiceLabel,
  planType,
}) => {
  // eslint-disable-next-line @altermaind/use-translations-scope
  const t = useTranslations('settings.tabs.plan');
  const { genericResponsiveValue } = useResponsiveStyle();
  const gap = parseFloat(`${useThemeValue('sizes', 'xs')}`);
  const gapM = parseFloat(`${useThemeValue('sizes', 'm')}`);
  const { openDialog: openContacts } = useDialog<DialogContactsPayload>('contacts');
  const { execute: executeGetBillingLink } = getBillingLinkCall.hook();

  const handleManagePlan = useCallback(() => {
    const { redirect, close } = openTabForAsyncRedirect();

    executeGetBillingLink({
      successSaga: ({ response }) => {
        if (response?.link) {
          redirect(response.link);
        } else {
          close();
        }
      },
      errorSaga: () => {
        close();
      },
    });
  }, [executeGetBillingLink]);

  return (
    <Card mt='xs'>
      <Box display='flex' flexDirection='column'>
        <Typography variant='h3' mb='s'>
          {t('details.title')}
        </Typography>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          style={{ gap }}
        >
          <Box
            flex={2}
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { mobile: 'column' },
              defaultValue: 'row',
            })}
            justifyContent='space-between'
            style={{
              gap: genericResponsiveValue({
                breakPoints: { mobile: gap },
                defaultValue: gapM,
              }),
            }}
          >
            <Box flex='0 0 auto' display='flex' flexDirection='column' style={{ gap }}>
              <DetailItem
                label={t('details.activationDate')}
                value={activationDateLabel}
                svgName='calendar'
                responsive={genericResponsiveValue}
              />
              <DetailItem
                label={t('details.billingType')}
                value={billingTypeLabel}
                svgName='creditCard'
                responsive={genericResponsiveValue}
              />
            </Box>
            <Box
              flex={1}
              display='flex'
              flexDirection='column'
              style={{ gap }}
              justifyContent={genericResponsiveValue({
                breakPoints: { tablet: 'flex-end' },
                defaultValue: 'none',
              })}
            >
              <DetailItem
                label={t('details.paymentMethod')}
                value={paymentMethodLabel}
                svgName='creditCard'
                responsive={genericResponsiveValue}
                justifyContent={genericResponsiveValue({
                  breakPoints: { tablet: 'flex-end' },
                  defaultValue: 'flex-start',
                })}
              />
              <DetailItem
                label={t('details.nextInvoice')}
                value={nextInvoiceLabel}
                svgName='calendar'
                responsive={genericResponsiveValue}
                justifyContent={genericResponsiveValue({
                  breakPoints: { tablet: 'flex-end' },
                  defaultValue: 'flex-start',
                })}
              />
            </Box>
          </Box>

          <Box flex={1}>
            <ClaimsComponent claimKey={Claims.BILLING_WRITE}>
              {planType === PlanType.CUSTOM ? (
                <>
                  <Typography variant='p2/light' color='grey/light'>
                    {t('yourPlan.offlineReviewOffer')}
                  </Typography>
                  <Typography
                    variant='p2'
                    color='primary'
                    style={{ cursor: 'pointer' }}
                    onClick={() => openContacts({})}
                  >
                    {t('yourPlan.offlineReviewOfferCta')}
                  </Typography>
                </>
              ) : (
                <Typography variant='p2/light' color='grey/light'>
                  {t('details.customNeeds')}
                  <Typography
                    as='span'
                    variant='p2'
                    color='primary'
                    style={{ cursor: 'pointer' }}
                    onClick={() => openContacts({})}
                  >
                    {` ${t('details.customNeedsCta')} `}
                  </Typography>
                  {t('details.customNeedsSuffix')}
                </Typography>
              )}
            </ClaimsComponent>
          </Box>
        </Box>
        <ClaimsComponent claimKey={Claims.BILLING_WRITE}>
          <Box flex={1}>
            <Typography
              variant='p2'
              color='primary'
              mt='xs'
              style={{ cursor: 'pointer' }}
              onClick={handleManagePlan}
            >
              {t('details.managePlanCta')}
            </Typography>
          </Box>
        </ClaimsComponent>

        <Box height={1} bg='grey/light/background' color='grey/light' my='l' />

        <IncludedInPlan />
      </Box>
    </Card>
  );
};
